import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useI18n } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop || attempts.current >= 10) { if (!stop) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <section className="section" style={{ paddingTop: 160, minHeight: "70vh" }}>
      <div className="container-x" style={{ maxWidth: 520 }}>
        <div className="card" style={{ padding: 40, textAlign: "center" }} data-testid="payment-result-card">
          {state === "checking" && (<>
            <Clock size={46} style={{ color: "var(--navy)", margin: "0 auto 14px" }} />
            <h2>{t({ es: "Confirmando su pago...", en: "Confirming your payment..." })}</h2>
            <p style={{ color: "var(--muted)" }}>{t({ es: "Un momento, estamos verificando con Stripe.", en: "One moment, we are verifying with Stripe." })}</p>
          </>)}
          {state === "paid" && (<>
            <CheckCircle2 size={50} style={{ color: "var(--wa)", margin: "0 auto 14px" }} />
            <h2 data-testid="payment-success-message">{t({ es: "¡Pago recibido!", en: "Payment received!" })}</h2>
            <p style={{ color: "var(--muted)" }}>{t({ es: "Gracias por su pago. Nuestro equipo procesará su pedido y le contactará a la brevedad.", en: "Thank you for your payment. Our team will process your order and contact you shortly." })}</p>
            <Link to="/" className="btn btn-navy" style={{ marginTop: 18 }}>{t({ es: "Volver al inicio", en: "Back to home" })}</Link>
          </>)}
          {state === "timeout" && (<>
            <Clock size={46} style={{ color: "#9a6b12", margin: "0 auto 14px" }} />
            <h2>{t({ es: "Pago en proceso", en: "Payment processing" })}</h2>
            <p style={{ color: "var(--muted)" }}>{t({ es: "Su pago está siendo procesado. Recibirá confirmación en breve; si tiene dudas escríbanos por WhatsApp.", en: "Your payment is being processed. You will receive confirmation shortly; contact us on WhatsApp if in doubt." })}</p>
          </>)}
          {state === "error" && (<>
            <XCircle size={46} style={{ color: "#c0392b", margin: "0 auto 14px" }} />
            <h2>{t({ es: "No pudimos confirmar el pago", en: "We could not confirm the payment" })}</h2>
            <p style={{ color: "var(--muted)" }}>{t({ es: "Si el cargo aparece en su tarjeta, contáctenos por WhatsApp con su comprobante.", en: "If your card was charged, contact us on WhatsApp with your receipt." })}</p>
          </>)}
        </div>
      </div>
    </section>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const estimateId = params.get("estimate_id");
  return (
    <section className="section" style={{ paddingTop: 160, minHeight: "70vh" }}>
      <div className="container-x" style={{ maxWidth: 520 }}>
        <div className="card" style={{ padding: 40, textAlign: "center" }} data-testid="payment-cancel-card">
          <XCircle size={46} style={{ color: "#9a6b12", margin: "0 auto 14px" }} />
          <h2>{t({ es: "Pago cancelado", en: "Payment cancelled" })}</h2>
          <p style={{ color: "var(--muted)" }}>{t({ es: "No se realizó ningún cargo. Puede intentarlo de nuevo cuando lo desee.", en: "No charge was made. You can try again whenever you like." })}</p>
          {estimateId && <Link data-testid="retry-payment-link" to={`/presupuesto/${estimateId}`} className="btn btn-navy" style={{ marginTop: 18 }}>{t({ es: "Volver al presupuesto", en: "Back to estimate" })}</Link>}
        </div>
      </div>
    </section>
  );
}
